import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Flag, 
  Plus,
  MapPin,
  Calendar,
  Users,
  Trophy,
  Clock,
  Loader2,
  Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RoundsPage = () => {
  const { user } = useAuth();
  const [myRounds, setMyRounds] = useState([]);
  const [publicRounds, setPublicRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-rounds');

  useEffect(() => {
    fetchRounds();
  }, []);

  const fetchRounds = async () => {
    try {
      const [myRes, publicRes] = await Promise.all([
        axios.get(`${API_URL}/rounds/my`),
        axios.get(`${API_URL}/rounds?status=live&limit=20`)
      ]);
      setMyRounds(myRes.data.rounds);
      setPublicRounds(publicRes.data.rounds);
    } catch (error) {
      console.error('Failed to fetch rounds:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'live':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'final':
        return 'bg-gold/20 text-gold border-gold/30';
      default:
        return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  const RoundCard = ({ round }) => (
    <Link to={`/rounds/${round.round_id}`}>
      <Card className="card-premium bg-white/5 border-white/10 hover-lift" data-testid={`round-${round.round_id}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <Badge className={getStatusColor(round.status)}>
              {round.status === 'live' && <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />}
              {round.status}
            </Badge>
            <Badge variant="outline" className="border-white/20 text-cream/70">
              {round.type}
            </Badge>
          </div>

          <div className="flex items-center gap-2 text-white mb-2">
            <MapPin className="w-4 h-4 text-gold" />
            <span className="font-semibold">
              {round.course?.name || 'Course TBD'}
            </span>
          </div>

          {round.course && (
            <p className="text-cream/60 text-sm mb-3">
              {round.course.city}, {round.course.state}
            </p>
          )}

          <div className="flex items-center gap-4 text-cream/70 text-sm mb-4">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {format(new Date(round.tee_time), 'MMM d, h:mm a')}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {round.player_uids?.length || 0} players
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Badge variant="outline" className="border-white/20 text-cream/60">
              {round.holes} holes
            </Badge>
            <Badge variant="outline" className="border-white/20 text-cream/60 capitalize">
              {round.format}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rounds-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-white mb-2">Rounds & Scoring</h1>
          <p className="text-cream/70">Track your rounds and compete with friends</p>
        </div>
        <Link to="/rounds/new">
          <Button className="btn-gold" data-testid="create-round-btn">
            <Plus className="w-4 h-4 mr-2" />
            Start New Round
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger 
            value="my-rounds"
            className="data-[state=active]:bg-gold data-[state=active]:text-deep-green"
          >
            <Flag className="w-4 h-4 mr-2" />
            My Rounds
          </TabsTrigger>
          <TabsTrigger 
            value="live"
            className="data-[state=active]:bg-gold data-[state=active]:text-deep-green"
          >
            <Play className="w-4 h-4 mr-2" />
            Live Rounds
          </TabsTrigger>
        </TabsList>

        <TabsContent value="my-rounds" className="mt-6">
          {myRounds.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-12 text-center">
                <Flag className="w-12 h-12 text-cream/30 mx-auto mb-4" />
                <p className="text-cream/60 mb-2">No rounds yet</p>
                <p className="text-cream/40 text-sm mb-6">Start your first round to track your scores</p>
                <Link to="/rounds/new">
                  <Button className="btn-gold">
                    <Plus className="w-4 h-4 mr-2" />
                    Start Round
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myRounds.map((round) => (
                <RoundCard key={round.round_id} round={round} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="live" className="mt-6">
          {publicRounds.length === 0 ? (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="py-12 text-center">
                <Play className="w-12 h-12 text-cream/30 mx-auto mb-4" />
                <p className="text-cream/60">No live rounds right now</p>
                <p className="text-cream/40 text-sm mt-1">Check back later to see active rounds</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publicRounds.map((round) => (
                <RoundCard key={round.round_id} round={round} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RoundsPage;
